void function (player) {
    const data = player.data;
    let storeObs = null; // intersection observer for avail visibility tracking
    player.trkData = {};
    // keep track of stores we've already seen through interception observer.
    // We disconnect observer and rebind all results after next requests are completed,
    // so an avail from initial request can have been tracked already and should not trigger a storeDisplayed event.
    let observedStores = [];
    let availAborter = null; // AbortController to cancel a running request
    // Constants for required async scripts
    const Dependencies = {
        TRACKER: 'tracker',
        SRE: 'sre',
        MAPPING: 'Mapping',
    };
    const AVAIL_SORT_ORDER = {
        PRIO_LIST: -1, // use retailer priority list
        DEFAULT: 0, // by retailer name, "alphabetically" (i.e. unicode codepoint)
        RANDOM: 1,
        PRICE: 2,
    };
    /** Map of filter names to filter value lists; used to build the availabilities request URL. */
    const availsReqFilters = new Map();
    /**
     * Fetches availabilities from the API.
     * @param forceLoc - Whether to force location-based search regardless of current location
     * @param filters - Optional filters to apply to the request
     */
    async function getAvails(forceLoc, filters) {
        let url;
        try {
            const { wtbid, isBundle } = data;
            const { product, location } = player.store;
            const country = location && location.country || data.country;
            if (!product)
                return;
            document.dispatchEvent(new CustomEvent('loader-start'));
            url = `https://wtb-api-hub.swaven.com/api/v4/availabilities/cart/${!isBundle ? product.ean : data.products.map((x) => x.ean).join(',')}?wtbid=${wtbid}&language=${data.locale}&requestedCountry=${country}&_internalUsage=true&nextEndpointV2=true&applyTiedHouseLaw=true`;
            // Provide the location, unless:
            // - we're in forceLoc mode (we want to use the API response)
            // - OR the location is the API default one
            if (location && !forceLoc && !location.isDefault) {
                if (location.lat)
                    url += `&latitude=${location.lat}`;
                if (location.lng)
                    url += `&longitude=${location.lng}`;
                if (location.country)
                    url += `&country=${location.country}`;
                if (location.zipCode)
                    url += `&zipCode=${location.zipCode}`;
                if (location.region)
                    url += `&state=${location.region}`;
                if (location.city)
                    url += `&city=${location.city}`;
            }
            // filters are stored and reused across requests
            if (filters)
                setAvailsFilters(filters);
            if (availsReqFilters.size > 0) {
                // control which parameters are included, don't blindly serialize the whole map
                const allowedFilters = ['classOfTrade'];
                let filtersFragment = '';
                availsReqFilters.forEach((value, key) => {
                    if (allowedFilters.includes(key))
                        filtersFragment += `&${key}=${value.join(',')}`;
                });
                url += filtersFragment;
            }
            if (data.draft)
                url += '&draft=1';
            // Native Experiences parameters
            if (data.iid)
                url += `&instanceId=${encodeURIComponent(data.iid)}`;
            if (player.baseTrk?.rfr2)
                url += `&currentUrl=${player.baseTrk.rfr2}`; // already encoded
            if (availAborter)
                availAborter.abort(); // cancel previous request
            availAborter = new AbortController();
            const res = await fetch(url, { credentials: 'include', signal: availAborter.signal });
            if (res.status != 200) {
                console.error(`avails fetch: error ${res.status}`); // eslint-disable-line no-console
                const responseText = await res.text();
                if (res.status === 400) {
                    try {
                        const resp = JSON.parse(responseText);
                        player.store.error = resp.detail;
                    }
                    catch (err) {
                        logger.error('Invalid error json', {
                            url,
                            status: res.status,
                            body: responseText,
                        });
                        player.store.error = '';
                    }
                }
                logger.error('availabilities fetch error', {
                    url,
                    status: res.status,
                    body: responseText,
                });
                return;
            }
            player.store.error = ''; // reset error state
            const response = await res.json();
            if (response.alcoholSettings) {
                player.store.showRetailerText = !!response.alcoholSettings.showRetailerText;
                if (response.alcoholSettings.showLocalRetailers != null) {
                    player.store.showLocalRetailers = !!response.alcoholSettings.showLocalRetailers;
                }
            }
            else {
                player.store.showLocalRetailers = null;
            }
            // APEX-27: Make additional requests if any
            const hasNext = response.next?.length > 0;
            // Request the full set of availabilities, including slower retailers
            if (hasNext) {
                getOtherRetailers(response.next[0], availAborter.signal, response.products);
            }
            if (typeof Mapping !== 'function')
                await awaitLoad(Dependencies.MAPPING);
            // mapping json data to AWE's own model.
            // Provided object is to replace macros in clkUrl.
            let avails = Mapping.mapAvailabilities(response.products, response.carts, {
                PAGEID: data.pageId,
                IID: data.iid,
                RFR: player.baseTrk.rfr,
                RFR2: player.baseTrk.rfr2,
            });
            if (!location || location.lat == null || !!forceLoc) {
                const apiloc = response.location;
                player.store.location = {
                    lat: apiloc.latitude,
                    lng: apiloc.longitude,
                    city: apiloc.city,
                    country: apiloc.country,
                    zipCode: apiloc.zipCode,
                    region: apiloc.state,
                    address: apiloc.address,
                    isDefault: apiloc._IsDefaultLocation,
                    origin: 'api',
                };
            }
            // Retailer view: group avails by typology or retailer id
            if (player.data.availsView === 'retailer') {
                avails = setRetailerView(avails);
                if (player.data.availSortOrder === AVAIL_SORT_ORDER.PRIO_LIST) {
                    avails.sort((a, b) => {
                        return compareBy(a, b, player.data.availSortOrder, player.data.retailerPrio, player.data.pinnedRetailers);
                    });
                }
            }
            player.store.avails = avails;
            document.dispatchEvent(new CustomEvent('availabilities-update', {
                detail: {
                    avails: player.store.avails,
                    fallbackStoreId: response._defaultStoreId,
                },
            }));
            player.trkData = {
                avgpc: response._analytics.averagePrice,
                avgdst: response._analytics.averageDistance,
                nbres: response._analytics.resultsCount,
                nbrefret: response._analytics.mappedRetailerCount,
                avreqid: uuid(),
            };
            // No additional requests to do: track avails update event and set avDone.
            if (!hasNext) {
                tracker.track('availabilities-update', { avails: player.store.avails, tab: player.store.selectedTab });
                player.store.avDone = true;
            }
            // reset current availability if not present if newly retrieved results.
            // if present, assign new object.
            if (player.store.currentAvail) {
                const curInNewAvails = player.store.avails
                    .find((x) => x.store.id === player.store.currentAvail.store.id);
                player.store.currentAvail = curInNewAvails || null;
            }
            observeStores(true);
            const tm = performance.getEntriesByName(url).pop();
            const v = tm ? Math.round(tm.duration) : null;
            tracker.track('apires', v ? { duration: v } : {});
            logger.log('api-avails', { duration: v });
            if (response._analytics.UnavailableStoreIds?.length > 0)
                tracker.track('unavlbsids', { sids: response._analytics.UnavailableStoreIds.slice(0, 20) });
        }
        catch (ex) {
            // ignore errors on request cancelling
            if (ex.name === 'AbortError')
                return;
            logger.error('getAvails failure', ex);
        }
        finally {
            document.dispatchEvent(new CustomEvent('loader-end'));
        }
    }
    player.getAvails = getAvails;
    /**
     * Merges filter key/values into the request map and refetches availabilities.
     * @param filters - The filters to set
     */
    function setAvailsFilters(filters) {
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                const arr = value;
                if (arr.length > 0)
                    availsReqFilters.set(key, arr);
                else
                    availsReqFilters.delete(key);
            });
            getAvails(false, undefined);
        }
    }
    /**
     * Fetches the next chunk of availabilities for slow/real-time retailers.
     * @param reqId - Next-endpoint request id from the initial response
     * @param signal - Abort signal tied to the current fetch chain
     * @param products - Product rows from the initial response (passed through for mapping)
     */
    async function getOtherRetailers(reqId, signal, products) {
        const url = `https://wtb-api-hub.swaven.com/api/v4/availabilities/cart/next/${reqId}`;
        try {
            const resp = await fetch(url, {
                credentials: 'include',
                signal,
            });
            const response = await resp.json();
            if (typeof Mapping !== 'function')
                await awaitLoad(Dependencies.MAPPING);
            let avails = Mapping.mapAvailabilities(products, response.carts, {
                PAGEID: data.pageId,
                IID: data.iid,
                RFR: player.baseTrk.rfr,
                RFR2: player.baseTrk.rfr2,
            });
            // Retailer view: group avails by typology or retailer id
            if (player.data.availsView === 'retailer') {
                avails = setRetailerView(avails);
            }
            player.store.avails = avails;
            document.dispatchEvent(new CustomEvent('availabilities-update', {
                detail: {
                    avails: player.store.avails,
                },
            }));
            player.trkData.avgpc = response._analytics.averagePrice;
            player.trkData.avgdst = response._analytics.averageDistance;
            player.trkData.nbres = response._analytics.resultsCount;
            player.trkData.nbrefret = response._analytics.mappedRetailerCount;
            observeStores(false);
            // Track retailer list displayed event for additional retailers (final count)
            tracker.track('availabilities-update', { avails: player.store.avails, tab: player.store.selectedTab });
            player.store.avDone = true;
        }
        catch (ex) {
            // ignore errors on request cancelling
            if (ex.name === 'AbortError')
                return;
            logger.error('getAvails failure', ex);
        }
    }
    /**
     * Retailer view: keep at most one availability per typology or retailer id (prefers entries with a click URL).
     * @param avails - Full availability list
     * @returns Deduplicated list in stable order
     */
    function setRetailerView(avails) {
        const filtered = new Map();
        avails.forEach((x) => {
            const typo = x.store.typologyId || x.retailer.id;
            const retStore = filtered.get(typo);
            // if no store/selected store for retailer has no clkUrl, try to replace it with one that has it.
            if (!retStore || !retStore.clkUrl && x.clkUrl) {
                filtered.set(typo, x);
            }
        });
        // Map values are returned by order of insertion, even if value for a key is updated.
        // Since process avails in order, the resulting array will keep the same order.
        return Array.from(filtered.values());
    }
    /**
     * Handles availability click: updates selection, opens the right destination, and emits tracking.
     * @param evt - DOM event if the action came from the UI; otherwise null
     * @param avail - Selected availability
     * @param mode - Redirect mode (e.g. map, phone, voucher); inferred from the target when omitted
     * @param allowPropagation - When true, does not stop propagation on `evt`
     */
    player.redirect = (evt, avail, mode, allowPropagation = false) => {
        if (evt) {
            // stop event propagation, unless explicitly allowed.
            if (!allowPropagation)
                evt.stopPropagation();
            evt.preventDefault();
        }
        if (avail != player.store.currentAvail)
            player.store.currentAvail = avail;
        if (!mode && evt)
            mode = evt.currentTarget.dataset.redirectMode;
        const openLink = (window.aweParams && window.aweParams.clickThrough) || window.open.bind(window);
        switch (mode) {
            case 'map':
                openLink(avail.store.gmapUrl);
                tracker.track('clkloc', { avail, outbound: true, auto: 0 }, TARGET_ALL);
                break;
            case 'googlemaps':
                openLink(evt?.currentTarget?.href);
                tracker.track('clkloc', { avail, clkloc_type: 'googlemaps', outbound: true, auto: 0 }, TARGET_ALL);
                break;
            case 'phone':
                openLink(`tel:${avail.store.phone}`);
                tracker.track('clkloc', { avail, clkloc_type: 'tel', auto: 0 }, TARGET_ALL);
                break;
            case 'whatsapp':
                openLink(`https://api.whatsapp.com/send?phone=${avail.store.whatsapp}&text=${player.data.remoteConf.whatsapp_message}`);
                tracker.track('clkloc', { avail, clkloc_type: 'whatsapp', auto: 0 }, TARGET_ALL);
                break;
            case 'appointment':
                openLink(avail.store.appointmentUrl);
                tracker.track('clkloc', { avail, clkloc_type: 'appointment', auto: 0 }, TARGET_ALL);
                tracker.track('appointment', { avail });
                break;
            case '2step':
                if (avail.voucher)
                    openLink(avail.voucher.secondStepUrl, '_self');
                tracker.track('2step', { avail }, TARGET_ALL);
                break;
            case 'url':
            case 'voucher':
            default: {
                const data = {
                    avail,
                    leadid: avail.leadid,
                    outbound: true,
                    auto: false,
                };
                if (mode === 'voucher') {
                    data.buy_type = mode;
                }
                tracker.track('buy', data, TARGET_ALL);
                // this.trigger('product-buy', {avail: avail, leadid: avail.leadid})
                openLink(avail.clkUrl || avail.store.url || avail.retailer.url);
                // generate a new leadid
                player.resetLeadid(avail);
            }
        }
    };
    /** Assigns a new lead id and refreshes click URLs for the given availability. */
    player.resetLeadid = (avail) => {
        if (!avail)
            return;
        avail.leadid = uuid().replace(/-/g, '');
        avail.clkUrl = avail.rawClkUrl.replace('{SWN-LEADID}', avail.leadid);
    };
    /**
     * Switches the active product and refetches availabilities.
     * @param evt - Custom event carrying optional index and auto-selection flag
     * @param idx - Product index override when not taken from `evt.detail`
     */
    player.selectProduct = (evt, idx) => {
        const pdtIdx = idx != null ? idx : evt.detail.idx;
        if (pdtIdx == null || player.store.product === data.products[pdtIdx])
            return;
        player.store.product = data.products[pdtIdx];
        player.trkData = {};
        getAvails(false, undefined);
        tracker.track('clk', { auto: evt.detail.auto, autoSel: evt.detail.auto });
    };
    /** IntersectionObserver callback: tracks stores once they cross the visibility threshold. */
    function intersectionCallback(entries, obs) {
        const ids = [];
        entries.forEach((entry) => {
            // an entry is sometimes included even if its ratio is below the threshold, so we check that explicitely
            if (entry.isIntersecting && entry.intersectionRatio >= obs.thresholds[0]) {
                obs.unobserve(entry.target); // no need to further observe an availability once it's been tracked
                const tgt = entry.target;
                ids.push(tgt.dataset.sid);
                observedStores.push(tgt.dataset.sid);
            }
        });
        if (ids.length) {
            const trk = { sids: ids };
            if (player.data.hasVouchers)
                trk.with_vouchers = player.store.avails.filter((x) => ids.includes(x.store.id) && x.voucher).map((x) => x.store.id).join(',');
            tracker.track('storeDisplayed', trk, TARGET_ALL);
        }
    }
    /**
     * Attaches intersection observers to availability rows for `storeDisplayed` tracking.
     * @param reset - When true, clears the set of stores already reported before re-observing
     */
    function observeStores(reset) {
        if (!window.IntersectionObserver)
            return;
        if (reset)
            observedStores = [];
        if (storeObs)
            storeObs.disconnect();
        else {
            storeObs = new IntersectionObserver(intersectionCallback, {
                root: null, // root node is viewport
                rootMargin: '0px',
                threshold: .5,
            });
        }
        // get all availability nodes and observe them
        const nds = document.getElementsByClassName('_avail');
        for (let i = 0; i < nds.length; i++) {
            const el = nds[i];
            if (!observedStores.includes(el.dataset.sid))
                storeObs.observe(el);
        }
    }
    /** Requests browser geolocation and updates the store location when reverse-geocoding succeeds. */
    function requestGeoloc() {
        navigator.geolocation.getCurrentPosition(pos => {
            if (pos && pos.coords) {
                getReverseGeocoding(pos.coords.longitude, pos.coords.latitude).then(location => {
                    // accept only if country is not OOS
                    if (location && player.data.countries.includes(location.country))
                        player.store.location = location;
                })
                    .catch(err => { console.error(err); }); // eslint-disable-line no-console
            }
        }, err => { console.warn(err.message); }, { timeout: 1e4 }); // eslint-disable-line no-console
    }
    /** Starts a geolocation request; on failure falls back to fetching avails with forced API location. */
    function autoloc() {
        try {
            requestGeoloc();
        }
        catch {
            getAvails(true, undefined);
        }
        finally {
            // TODO: track geoloc
        }
    }
    player.autoloc = autoloc;
    /**
     * Reverse-geocodes coordinates via Mapbox and returns a normalized `Location`.
     * @param lng - Longitude
     * @param lat - Latitude
     * @param countries - Optional country filter for the geocoder
     * @param language - Optional language hint for the geocoder
     */
    async function getReverseGeocoding(lng, lat, countries = [], language = null) {
        let qs = 'access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA';
        if (countries.length > 0)
            qs += `&country=${typeof countries === 'string' ? countries : countries.join(',')}`;
        if (language)
            qs += `&language=${language}`;
        try {
            const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${qs}`), data = await res.json();
            if (res.status >= 400)
                throw new Error(data.message);
            if (data.features.length === 0)
                return null;
            return parseFeature(data.features[0]);
        }
        catch (ex) {
            console.error(ex); // eslint-disable-line no-console
            return null;
        }
        finally {
            // track mapbox usage
            tracker.track('mapbox-request', { api: 'geocode', query: `${lng},${lat}` }, TARGET_LOG);
        }
    }
    player.getReverseGeocoding = getReverseGeocoding;
    /** Maps a Mapbox geocoding feature into an internal `Location` shape. */
    function parseFeature(feat) {
        if (!feat)
            return null;
        const place = {
            lng: feat.center[0],
            lat: feat.center[1],
            country: '',
            zipCode: '',
            city: '',
            address: feat.place_name,
            region: '',
            isDefault: false,
            origin: null,
        };
        if (feat.place_type.includes('postcode'))
            place.zipCode = feat.text;
        if (feat.place_type.includes('country') && feat.properties.short_code)
            place.country = feat.properties.short_code.toUpperCase();
        if (feat.place_type.includes('region') && feat.properties.short_code)
            place.region = feat.properties.short_code.toUpperCase();
        if (feat.place_type.includes('place'))
            place.city = feat.text;
        if (feat.context) {
            feat.context.forEach((context) => {
                const type = context.id.split('.')[0];
                // Context list is ordered, country will override region for setting place.country.
                if ((type === 'country' || type === 'region' && !place.country) && !!context.short_code)
                    place.country = context.short_code.toUpperCase();
                else if (type === 'postcode' && !place.zipCode)
                    place.zipCode = context.text;
                if ((type === 'region' || type === 'place' && !place.region) && !!context.short_code) {
                    place.region = context.short_code.toUpperCase();
                }
                if (type === 'place' && !place.city)
                    place.city = context.text;
            });
        }
        // remove country prefix from region code.
        // Cannot be done directly when setting region, as context list is ordered by size,
        // country may not be known at that time.
        if (place.region && place.country)
            place.region = place.region.replace(new RegExp(`^${place.country}[-_\s]?`, 'i'), ''); // eslint-disable-line no-useless-escape
        return place;
    }
    player.parseFeature = parseFeature;
    /**
     * Compares two availabilities for sorting (price, random, priority list, or name).
     * Pinned retailers always sort before non-pinned when a pin list is provided.
     */
    function compareBy(availA, availB, method, retailerPrio, pinnedRetailers) {
        // avails from pinned retailers always go on top
        if (pinnedRetailers) {
            const pinIdxA = pinnedRetailers.indexOf(availA.retailer.id);
            const pinIdxB = pinnedRetailers.indexOf(availB.retailer.id);
            if (pinIdxA != -1 && pinIdxB == -1)
                return -1;
            else if (pinIdxA == -1 && pinIdxB != -1)
                return 1;
            else if (pinIdxA != -1 && pinIdxB != -1) // both are pinned: use order in list
                return pinIdxA - pinIdxB;
            // both are non-pinned: continue and use default sort method
        }
        if (method === AVAIL_SORT_ORDER.PRICE)
            return availA.price.amount - availB.price.amount;
        else if (method === AVAIL_SORT_ORDER.RANDOM)
            return Math.random() - 0.5; // returns number in the [-0.5, +0.5[ range
        else if (method === AVAIL_SORT_ORDER.PRIO_LIST) {
            if (!retailerPrio)
                throw new Error('Missing retailer priority list');
            const iA = retailerPrio.indexOf(availA.retailer.id);
            const iB = retailerPrio.indexOf(availB.retailer.id);
            if (iA === -1 && iB > -1)
                return 1;
            else if (iA > -1 && iB === -1)
                return -1;
            return iA - iB;
        }
        else {
            if (availA.retailer.name < availB.retailer.name)
                return -1;
            else if (availA.retailer.name > availB.retailer.name)
                return 1;
            return 0;
        }
    }
    player.compareBy = compareBy;
    /** Emits navigation timing metrics to the logger after page load. */
    function sendLoadMetrics() {
        const perf = performance.getEntriesByType('navigation')[0];
        logger.log('load', {
            transferSize: perf && perf.transferSize,
            domLoaded: perf && Math.round(perf.duration),
            domContentLoaded: perf && Math.round(perf.domContentLoadedEventStart),
            latency: perf && Math.round(perf.responseEnd), // startTime is always 0 for a navigation entry
        });
    }
    /** SRE watcher: refetches avails on user-driven location changes and broadcasts `location-update`. */
    function locationUpdated(location) {
        // no need to make new request if location update already comes from api
        if (location.origin !== 'api')
            getAvails(false, undefined);
        document.dispatchEvent(new CustomEvent('location-update', { detail: { location }, bubbles: true }));
    }
    /** SRE watcher: dispatches `availability-select` when the current availability changes. */
    function currentAvailUpdated(avail) {
        document.body.dispatchEvent(new CustomEvent('availability-select', { detail: { avail }, bubbles: true }));
    }
    /**
     * Returns a URL for driving directions from the user's current location to the store.
     * Uses `avail.store.directionsUrl` with origin placeholders when present; otherwise builds a Google Maps URL.
     * @param avail - Availability containing store and optional `directionsUrl`
     * @returns Directions URL, or `'#'` when data is incomplete
     */
    function getDirectionsUrl(avail) {
        if (!avail || !avail.store?.location)
            return '#';
        let directionsUrl;
        // Use API-provided directions URL if available
        if (avail.store.directionsUrl) {
            directionsUrl = avail.store.directionsUrl
                .replace('{ORIGIN_LAT}', player.store.location.lat || 0)
                .replace('{ORIGIN_LNG}', player.store.location.lng || 0);
        }
        else {
            // Fallaback to manual url building
            directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getStoreFullname(avail))}&origin=${encodeURIComponent(player.store.location.address)}`;
        }
        return directionsUrl;
    }
    player.getDirectionsUrl = getDirectionsUrl;
    /**
     * Builds a single-line label: retailer (if distinct), store name, and address fields.
     * @param avail - Availability to read from, or undefined
     */
    function getStoreFullname(avail) {
        if (!avail)
            return '';
        let elems = [];
        if (avail.store.name)
            elems.push(avail.store.name);
        // if retailer name must be added, let it be before store name.
        if (avail.retailer.name && !avail.store.name?.startsWith(avail.retailer.name))
            elems.unshift(avail.retailer.name);
        elems = elems.concat([
            avail.store.location.address,
            avail.store.location.city,
            avail.store.location.zipCode,
        ]);
        return elems.join(' ');
    }
    /**
     * Polls until a dependency global (e.g. tracker, Mapping) is available or timeout is reached.
     * @param objName - Key in `Dependencies` or other global name
     * @throws When the dependency does not appear within the wait budget
     */
    async function awaitLoad(objName) {
        const MAX_WAIT = 30e3; // max wait time in ms
        const BACKOFF_RATIO = 1.5; // to grow the wait interval each round
        let itv = 10; // initial wait interval in ms
        let loaded = false;
        const start = performance.now();
        do {
            await new Promise(resolve => { setTimeout(resolve, itv); });
            switch (objName) {
                case Dependencies.MAPPING:
                    // Mapping is a class, not a global object
                    loaded = typeof Mapping === 'function';
                    break;
                default:
                    loaded = window[objName] != null;
            }
            itv *= BACKOFF_RATIO;
        } while (!loaded && performance.now() - start < MAX_WAIT);
        const duration = performance.now() - start;
        if (loaded)
            return;
        else
            throw new Error(`awaitLoad timeout: ${objName} not found after ${Math.round(duration)}ms`);
    }
    /* ************************************************************
                      Entry point
     **************************************************************/
    /** Bootstraps tracking, SRE, initial avails, geolocation, and load metrics. */
    void async function init() {
        if (player.data.touchpoint === 'IP') {
            if (!window.tracker)
                await awaitLoad(Dependencies.TRACKER);
            tracker.track('dploy', { auto: true }, TARGET_ALL);
            tracker.track('visible');
            tracker.track('clk', { auto: true, autoSel: true });
        }
        // default watchers
        if (!window.sre)
            await awaitLoad(Dependencies.SRE);
        sre.watch({
            location: locationUpdated,
            currentAvail: currentAvailUpdated,
        });
        await sre.start(player, window.logger);
        document.body.addEventListener('mouseenter', () => {
            tracker.track('mover');
        }, { once: true, passive: true });
        if (player.data.touchpoint === 'IP')
            getAvails(false, undefined);
        if (!data.remoteConf.noAutoGeoloc && !data.media) {
            try {
                requestGeoloc();
            }
            catch (ex) {
                console.warn(ex);
            } // eslint-disable-line no-console
        }
        // bind 'view all' button
        const moreBtn = document.getElementById('more');
        if (moreBtn) {
            moreBtn.addEventListener('click', (e) => {
                document.getElementById('avails-ctnr')?.classList.toggle('show-all');
                const t = e.currentTarget;
                const lbl = t.textContent;
                t.textContent = t.dataset.altLabel || '';
                t.dataset.altLabel = lbl;
            });
        }
        if (document.readyState === 'complete')
            sendLoadMetrics();
        else {
            // add a delay because perf duration is not set until the load handlers have run.
            // We don't use that event, so all handlers should run in less than 200ms.
            window.addEventListener('load', () => {
                setTimeout(sendLoadMetrics, 200);
            }, { once: true });
        }
    }();
}(window.player);
/**
 * Replaces `{key}` placeholders in `txt` with values from `replacements`.
 * @param txt - Template string
 * @param replacements - Map of placeholder names to replacement values
 */
window.fillString = function (txt, replacements) {
    let res = txt;
    Object.entries(replacements).forEach(([key, val]) => {
        res = res.replace(new RegExp(`{${key}}`, 'g'), val);
    });
    return res;
};
